import Modal from '../../../components/Modal';
import FormGenerator from '../../../components/FormGenerator';
import SelectorEngine from '../../../utils/dom/selector-engine';
import EventHandler from '../../../utils/dom/event-handler';
import Request, { ApiMode } from '../../../utils/request';
import { startLoadingAnimation, stopLoadingAnimation } from '../../../utils/dom/loading';
import Manipulator from '../../../utils/dom/manipulator';
import { venueRegistrationQuestionRenderers } from '../formQuestions';

const KEY_OBJECT = 'fieldName';
const VALUE_OBJECT = 'value';
const CLICK_EVENT = 'click';

const DEFAULT_CONFIG = {
	wrapperSelector: '.p-program-landing',
	sectionWrapperSelector: '.p-program-landing-wrapper',
	errorSnackbarSelector: 'lta-error-snackbar',
	isLocal: false,
	logPrefix: 'VenueRegistrationFormModal'
};

/**
 * Base class for venue-registration forms that open in a modal, fetch steps,
 * render via FormGenerator, and submit answers to configured endpoints.
 *
 * Subclasses must implement `getFormGeneratorOptions(stepsData)`.
 * Optionally override `afterFormGeneratorInit()` for form-specific wiring.
 */
class VenueRegistrationFormModal {
	/**
	 * @param {Object} config
	 * @param {string} config.modalSelector
	 * @param {string[]} config.triggerSelectors - CSS selectors for open-form buttons
	 * @param {string[]} [config.editTriggerSelectors] - CSS selectors for edit buttons
	 * @param {string} config.stepsEndpointAttr - data-* attribute name for the steps URL
	 * @param {string} config.submitEndpointAttr - data-* attribute name for the submit URL
	 * @param {string} [config.wrapperSelector]
	 * @param {string|null} [config.sectionWrapperSelector] - nested element holding endpoint data attrs; `null` uses the page wrapper
	 * @param {string} [config.errorSnackbarSelector]
	 * @param {boolean} [config.isLocal]
	 * @param {string} [config.logPrefix]
	 */
	constructor(config) {
		this.config = { ...DEFAULT_CONFIG, ...config };

		this._wrapper = SelectorEngine.findOne(this.config.wrapperSelector);
		if (!this._wrapper) {
			return;
		}

		this.init();
	}

	get stepsData() {
		return this._stepsData;
	}

	set stepsData(_stepsData) {
		this._stepsData = _stepsData;
		if (_stepsData?.length > 0) {
			this.initiateFormGenerator(_stepsData);
		}
	}

	init() {
		// Prefer a nested section wrapper when configured; otherwise read endpoints from the page wrapper
		this.sectionWrapper = this.config.sectionWrapperSelector
			? SelectorEngine.findOne(this.config.sectionWrapperSelector, this._wrapper) || this._wrapper
			: this._wrapper;

		this.stepsEndpoint = Manipulator.getDataAttribute(this.sectionWrapper, this.config.stepsEndpointAttr);
		this.submitEndpoint = Manipulator.getDataAttribute(this.sectionWrapper, this.config.submitEndpointAttr);

		this.formModalComponent = SelectorEngine.findOne(this.config.modalSelector, this._wrapper);
		if (!this.formModalComponent) {
			return;
		}

		this.errorSnackbar = SelectorEngine.findOne(this.config.errorSnackbarSelector, this.formModalComponent);

		(this.config.triggerSelectors || []).forEach((selector) => {
			this.bindDelegatedFormTrigger(selector, 'add');
		});

		(this.config.editTriggerSelectors || []).forEach((selector) => {
			this.bindDelegatedFormTrigger(selector, 'edit');
		});
	}

	bindDelegatedFormTrigger(selector, mode = 'add') {
		if (!selector) {
			return;
		}

		EventHandler.on(this._wrapper, CLICK_EVENT, selector, (event) => {
			if (event) {
				event.preventDefault();
			}
			this.contactId = mode === 'edit' ? this.getContactIdFromTrigger(event) : undefined;
			this.openFormModal(mode);
		});
	}

	showError(message) {
		this.errorSnackbar?.show(message);
		this.errorSnackbar?.scrollIntoView({
			behavior: 'smooth',
			block: 'center'
		});
	}

	hideError() {
		this.errorSnackbar?.hide();
	}

	getResponseErrorMessage(response) {
		return response?.errorMessage || response?.error?.errorMessage || null;
	}

	handleSubmitFailure(errorMessage) {
		this.showError(errorMessage);
		this.formGenerator?.enableSubmitButton();
	}

	/**
	 * Returns the request params to send with the steps API call.
	 * Override in child classes to provide module-specific request params.
	 * @returns {Object}
	 */
	getRequestParam() {
		if (this.formMode !== 'edit') {
			return {};
		}

		return {
			mode: 'edit',
			...(this.contactId ? { contactId: this.contactId } : {})
		};
	}

	getContactIdFromTrigger(event) {
		const trigger = event?.delegateTarget || event?.target;
		if (!trigger) {
			return undefined;
		}

		const card = trigger.closest?.('lta-edit-card-info');

		return (
			Manipulator.getDataAttribute(trigger, 'contact-id') ||
			card?.getAttribute?.('contact-id') ||
			(card && Manipulator.getDataAttribute(card, 'contact-id')) ||
			undefined
		);
	}

	openFormModal(mode = 'add') {
		if (!this.formModalComponent?.modalEl) {
			return;
		}

		this.formMode = mode;

		if (!this.formModalInstance) {
			this.formModalInstance = new Modal(this.formModalComponent.modalEl, { backdrop: 'static' });
			this.formModalInstance.isEscapeEventEnable = false;
		}

		if (this.formGenerator) {
			this.formGenerator.unload();
			this.formGenerator = null;
		}

		this.hideError();
		this.referenceId = undefined;
		if (this.formMode !== 'edit') {
			this.contactId = undefined;
		}
		this.formModalInstance.show();
		this.fetchProgramSteps();
	}

	closeFormModal() {
		if (this.searchObserver) {
			this.searchObserver.disconnect();
			this.searchObserver = null;
		}

		this.hideError();
		this.formGenerator?.unload();
		this.formGenerator = null;
		this.formModalInstance?.hide();
	}

	/**
	 * Subclasses must return FormGenerator options (formType, buttons, etc.).
	 * Shared defaults (theme, exit button, progress) are applied in initiateFormGenerator.
	 * @param {Array} stepsData
	 * @returns {Object}
	 */
	getFormGeneratorOptions(stepsData) {
		throw new Error(`${this.constructor.name} must implement getFormGeneratorOptions()`);
	}

	/**
	 * Hook called after FormGenerator is constructed and listeners are bound.
	 * Override for form-specific button wiring or behaviour tweaks.
	 */
	afterFormGeneratorInit() {}

	/**
	 * Returns the URL to submit answers to. Override to switch endpoints based on
	 * form state (e.g. manual entry vs. search-selected venue).
	 * @returns {string}
	 */
	getSubmitEndpoint() {
		return this.submitEndpoint;
	}

	/**
	 * Builds the POST body for submitProgramDetails(). Override to reshape the payload
	 * for a specific endpoint (e.g. unwrapping a question's answer from its `fieldName` key).
	 * @returns {Object}
	 */
	getSubmitPayload() {
		return {
			...this.answerList,
			...(this.referenceId ? { referenceId: this.referenceId } : {}),
			...(this.formMode === 'edit' && this.contactId ? { contactId: this.contactId } : {})
		};
	}

	initiateFormGenerator(stepsData) {
		if (!this.formModalComponent?.contentEl) {
			return;
		}

		if (this.formGenerator) {
			this.formGenerator.unload();
			this.formGenerator = null;
		}

		this.formModalComponent.contentEl.innerHTML = '';
		this.formModalComponent.contentEl.classList.remove('lta-form-modal__content--success');

		const formGeneratorOptions = this.getFormGeneratorOptions(stepsData);

		if (formGeneratorOptions.successScreen) {
			this.config.successScreen = formGeneratorOptions.successScreen;
		}

		this.formGenerator = new FormGenerator(this.formModalComponent.contentEl, {
			questions: stepsData,
			themeColor: 'color-lta-blue',
			isBackButtonEnabled: false,
			isHideProgress: true,
			isExitButtonEnabled: true,
			exitButtonClasses: 'lta-form-generator__form-exit-button--underlined',
			exitButtonLabel: 'Save and exit',
			exitButtonHandler: () => this.saveExitClickHandler(),
			questionRenderers: venueRegistrationQuestionRenderers,
			...formGeneratorOptions
		});

		if (this.formGenerator?.topBackButton) {
			if (formGeneratorOptions.showBackButtonOnFirstStep) {
				this.formGenerator.topBackButton.show();
			} else {
				this.formGenerator.topBackButton.hide();
			}
		}

		this.formGenerator.onChange((answers) => {
			this.prepareRequestObject(answers);
		});

		this.formGenerator.onStepChange((step) => {
			if (this.formGenerator?.topBackButton) {
				const shouldShow = step !== 1 || formGeneratorOptions.showBackButtonOnFirstStep;
				shouldShow ? this.formGenerator.topBackButton.show() : this.formGenerator.topBackButton.hide();
			}
			this.saveExitClickHandler(false);
		});

		this.formGenerator.onSubmit((answers) => {
			this.prepareRequestObject(answers);
			this.submitProgramDetails();
		});

		this.afterFormGeneratorInit();
	}

	isCurrentFormStepValid() {
		return this.formGenerator.stepValidator(this.formGenerator.currentStep);
	}

	async saveExitClickHandler(shouldModalClose = true) {
		if (!this.formGenerator) {
			return;
		}

		if (shouldModalClose) {
			const isValid = await this.isCurrentFormStepValid();
			if (!isValid) {
				return;
			}
		}

		if (!this.shouldExitWithoutSaving) {
			this.prepareRequestObject(this.formGenerator.answers);
			await this.submitProgramDetails(shouldModalClose);
			return;
		}

		if (shouldModalClose) {
			this.closeFormModal();
		}
	}

	async fetchProgramSteps() {
		startLoadingAnimation();
		try {
			if (!this.stepsEndpoint) {
				stopLoadingAnimation();
				return;
			}

			const requestParams = this.getRequestParam();

			const response = await Request.get(
				this.stepsEndpoint,
				requestParams,
				{
					local: this.config.isLocal,
					req: {
						apiMode: ApiMode.LtaError
					}
				}
			);

			const { Steps, totalSteps, errorMessage, shouldExitWithoutSaving, referenceId, checkoutData } = response;

			stopLoadingAnimation();

			if (errorMessage) {
				this.showError(errorMessage);
				return;
			}

			// Some form flows (e.g. submit/payment) need derived pricing inputs to build the correct questions.
			// If the backend returns `checkoutData`, make sure it's available before `stepsData` triggers render.
			if (checkoutData) {
				this.checkoutData = checkoutData;
			}

			// Default true so existing forms keep saving on exit / step change
			this.shouldExitWithoutSaving = shouldExitWithoutSaving;
			this.referenceId = referenceId || undefined;

			if (Steps?.length) {
				this.totalSteps = totalSteps || Math.max(...Steps.map((step) => step.step));
				this.stepsData = Steps;
			}
		} catch (error) {
			stopLoadingAnimation();
			// eslint-disable-next-line no-console
			console.error(`${this.config.logPrefix} – fetch steps error:`, error);
		}
	}

	async submitProgramDetails(shouldModalClose = true) {
		startLoadingAnimation();
		this.hideError();

		try {
			const submitEndpoint = this.getSubmitEndpoint();

			if (!submitEndpoint) {
				this.closeFormModal();
				stopLoadingAnimation();
				return false;
			}

			const payload = this.getSubmitPayload();

			const response = await Request.post(submitEndpoint, payload, {
				local: this.config.isLocal,
				req: {
					apiMode: ApiMode.LtaError
				}
			});

			const { Success, success, redirectUrl, redirectURL } = response;
			const errorMessage = this.getResponseErrorMessage(response);
			const nextUrl = redirectUrl || redirectURL;

			stopLoadingAnimation();

			if (errorMessage && shouldModalClose) {
				this.handleSubmitFailure(errorMessage);
				return false;
			}

			if ((Success || success) && shouldModalClose) {
				if (nextUrl) {
					this.goToUrl(nextUrl);
					return true;
				}

				if (this.config.successScreen) {
					this.showSuccessScreen();
					return true;
				}

				this.closeFormModal();
				window.location.reload();
			}

			if (shouldModalClose && !(Success || success)) {
				this.formGenerator?.enableSubmitButton();
			}

			return !!(Success || success);
		} catch (error) {
			stopLoadingAnimation();
			const errorMessage = this.getResponseErrorMessage(error) || 'Unable to save. Please try again.';

			if (shouldModalClose) {
				this.handleSubmitFailure(errorMessage);
			}

			// eslint-disable-next-line no-console
			console.error(`${this.config.logPrefix} – submit error:`, error?.message || error);
			return false;
		}
	}

	/**
	 * Renders the shared `lta-success-screen` web component inside the modal instead of
	 * closing it, using `config.successScreen` ({ eyebrow, title, description, content, buttonLabel }).
	 * Closes (and reloads) the modal when the success screen's close button is clicked.
	 */
	showSuccessScreen() {
		if (!this.formModalComponent?.contentEl) {
			return;
		}

		this.formGenerator?.unload();
		this.formGenerator = null;

		const { eyebrow, title, description, content = '', buttonLabel } = this.config.successScreen;
		const host = this.formModalComponent.contentEl;

		host.classList.add('lta-form-modal__content--success');
		host.innerHTML = `
			<lta-success-screen
				eyebrow="${eyebrow || 'Congratulations'}"
				title="${title || ''}"
				description="${description || ''}"
				button-label="${buttonLabel || 'Close'}"
			>
				${content}
			</lta-success-screen>
		`;

		const successScreen = SelectorEngine.findOne('lta-success-screen', host);
		if (successScreen) {
			EventHandler.on(successScreen, 'lta-success-screen:close', () => {
				this.closeFormModal();
				window.location.reload();
			});
		}
	}

	goToUrl(redirectUrl) {
		if (!redirectUrl) {
			return;
		}

		if (/^https?:\/\//i.test(redirectUrl)) {
			window.location.href = redirectUrl;
			return;
		}

		window.location.href = `${window.location.origin}${redirectUrl}`;
	}

	prepareRequestObject(stepsAnswers) {
		this.answerList = {};

		for (const stepKey of Object.keys(stepsAnswers)) {
			const stepAnswersObjects = stepsAnswers[stepKey];

			for (const questionKey of Object.keys(stepAnswersObjects)) {
				const answerObject = stepAnswersObjects[questionKey];

				if (!answerObject) {
					continue;
				}

				const formatObject = {};
				formatObject[answerObject[KEY_OBJECT]] = answerObject[VALUE_OBJECT];
				this.answerList = {
					...this.answerList,
					...formatObject
				};
			}
		}
	}

	/**
	 * Bootstrap helper – waits for `lta-form-modal` then constructs the form class.
	 * @param {typeof VenueRegistrationFormModal} FormClass
	 */
	static boot(FormClass) {
		const start = () => requestAnimationFrame(() => new FormClass());

		if (customElements.get('lta-form-modal')) {
			start();
		} else {
			customElements
				.whenDefined('lta-form-modal')
				.then(start)
				.catch(() => {});
		}
	}
}

export default VenueRegistrationFormModal;
